import { useState, type CSSProperties } from 'react';
import { Download, ImagePlus, Maximize2 } from 'lucide-react';
import type { ImageAspectRatio } from '../../../types/gallery';
import { useAddGalleryImageMutation } from '../../../features/gallery/mutations/useGalleryImageMutations';
import { usePlatformChatMessageAttachmentDataQuery } from '../../../features/chat/queries/usePlatformChatMessageAttachmentDataQuery';
import { useBlobObjectUrl } from '../../../hooks/useBlobObjectUrl';
import { useElementVisibility } from '../../../hooks/useElementVisibility';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import { LiksChatMessageImageMetaBadges } from './LiksChatMessageImageMetaBadges';
import { LiksChatMessageImageActionButton } from './LiksChatMessageImageActionButton';
import { LiksChatMessageImagePreviewModal } from './LiksChatMessageImagePreviewModal';
import './LiksChatMessageGeneratedImage.css';

function resolveImageAspectRatioCss(
  imageAspectRatio?: string | null,
): CSSProperties {
  const ratio: ImageAspectRatio =
    imageAspectRatio === '16:9' ||
    imageAspectRatio === '9:16' ||
    imageAspectRatio === '3:4' ||
    imageAspectRatio === '1:1'
      ? imageAspectRatio
      : '1:1';

  const cssRatio =
    ratio === '16:9'
      ? '16 / 9'
      : ratio === '9:16'
        ? '9 / 16'
        : ratio === '3:4'
          ? '3 / 4'
          : '1 / 1';

  return {
    '--liks-chat-image-aspect-ratio': cssRatio,
  } as CSSProperties;
}

export type LiksChatMessageGeneratedImageProps = {
  sessionId: string;
  messageId: string;
  attachmentId: string;
  galleryImageId?: string | null;
  alt: string;
  title?: string | null;
  imageFormat?: string | null;
  imageAspectRatio?: string | null;
  fileName?: string;
  downloadLabel: string;
  expandLabel: string;
  closePreviewLabel: string;
  loadingLabel: string;
  /** When true, shows save-to-gallery next to download (platform chat assistant images). */
  enableSaveToGallery?: boolean;
  saveToGalleryLabel?: string;
};

export function LiksChatMessageGeneratedImage({
  sessionId,
  messageId,
  attachmentId,
  galleryImageId,
  alt,
  title,
  imageFormat,
  imageAspectRatio,
  fileName,
  downloadLabel,
  expandLabel,
  closePreviewLabel,
  loadingLabel,
  enableSaveToGallery = false,
  saveToGalleryLabel,
}: LiksChatMessageGeneratedImageProps) {
  const { translate } = useI18n();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const addToGalleryMutation = useAddGalleryImageMutation();
  const { ref: visibilityRef, hasBeenVisible } = useElementVisibility();
  const attachmentDataQuery = usePlatformChatMessageAttachmentDataQuery(
    sessionId,
    messageId,
    attachmentId,
    { enabled: hasBeenVisible },
  );
  const objectUrl = useBlobObjectUrl(attachmentId, attachmentDataQuery.data);
  const frameStyle = resolveImageAspectRatioCss(imageAspectRatio);

  const resolvedSaveToGalleryLabel =
    saveToGalleryLabel ?? translate('platformChat.saveToGallery');
  const resolvedGalleryImageId = galleryImageId?.trim() || null;
  const showSaveToGallery =
    enableSaveToGallery && resolvedGalleryImageId != null;

  const handleDownload = () => {
    if (!objectUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName?.trim() || 'generated-image';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleSaveToGallery = () => {
    if (
      !showSaveToGallery ||
      resolvedGalleryImageId == null ||
      addToGalleryMutation.isPending
    ) {
      return;
    }

    addToGalleryMutation.mutate(resolvedGalleryImageId, {
      onSuccess: () => {
        toast.success({
          title: translate('platformChat.saveToGallerySuccessTitle'),
          description: translate(
            'platformChat.saveToGallerySuccessDescription',
          ),
        });
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('platformChat.saveToGalleryErrorTitle'),
          translate('platformChat.saveToGalleryErrorDescription'),
        );
      },
    });
  };

  const showLoading =
    !hasBeenVisible ||
    attachmentDataQuery.isLoading ||
    (attachmentDataQuery.isSuccess && objectUrl == null);
  const showError = hasBeenVisible && attachmentDataQuery.isError;

  if (showError) {
    return null;
  }

  const saveToGalleryButton = showSaveToGallery ? (
    <LiksChatMessageImageActionButton
      label={resolvedSaveToGalleryLabel}
      disabled={addToGalleryMutation.isPending}
      onClick={handleSaveToGallery}
    >
      <ImagePlus
        className="liks-chat-message__image-action-icon"
        strokeWidth={2}
        aria-hidden
      />
    </LiksChatMessageImageActionButton>
  ) : null;

  return (
    <>
      <div
        ref={visibilityRef}
        className={[
          'liks-chat-message__image-frame',
          showLoading && 'liks-chat-message__image-frame--loading',
        ]
          .filter(Boolean)
          .join(' ')}
        style={frameStyle}
        aria-busy={showLoading && hasBeenVisible ? true : undefined}
        aria-label={showLoading && hasBeenVisible ? loadingLabel : undefined}
      >
        {showLoading ? (
          hasBeenVisible ? (
            <div className="liks-chat-message__spinner" aria-hidden />
          ) : null
        ) : (
          <>
            <img
              className="liks-chat-message__image"
              src={objectUrl!}
              alt={alt}
              loading="lazy"
            />
            <div className="liks-chat-message__image-actions">
              <LiksChatMessageImageActionButton
                label={downloadLabel}
                onClick={handleDownload}
              >
                <Download
                  className="liks-chat-message__image-action-icon"
                  strokeWidth={2}
                  aria-hidden
                />
              </LiksChatMessageImageActionButton>
              {saveToGalleryButton}
              <LiksChatMessageImageActionButton
                label={expandLabel}
                onClick={() => setIsPreviewOpen(true)}
              >
                <Maximize2
                  className="liks-chat-message__image-action-icon"
                  strokeWidth={2}
                  aria-hidden
                />
              </LiksChatMessageImageActionButton>
            </div>
            <LiksChatMessageImageMetaBadges
              imageFormat={imageFormat}
              imageAspectRatio={imageAspectRatio}
              variant="overlay"
            />
          </>
        )}
      </div>

      <LiksChatMessageImagePreviewModal
        isOpen={isPreviewOpen && objectUrl != null}
        imageUrl={objectUrl ?? ''}
        alt={alt}
        title={title}
        imageFormat={imageFormat}
        imageAspectRatio={imageAspectRatio}
        downloadLabel={downloadLabel}
        onDownload={handleDownload}
        saveToGalleryLabel={
          showSaveToGallery ? resolvedSaveToGalleryLabel : undefined
        }
        onSaveToGallery={showSaveToGallery ? handleSaveToGallery : undefined}
        isSaveToGalleryPending={addToGalleryMutation.isPending}
        closeLabel={closePreviewLabel}
        onClose={() => setIsPreviewOpen(false)}
      />
    </>
  );
}
